import React, { useEffect, useRef, useState } from 'react';
import { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Provider } from 'react-redux';
import { useLocation } from 'react-use';
import { default as ReactGA4 } from 'react-ga4';
// @ts-ignore: Unreachable code error
import Log from '@zjs/common/log';
import cn from 'classnames';
import {
    COOKIE_KEY,
    DEVICES,
    GA4_TRACKING_MOBILE_ID,
    GA4_TRACKING_ZALO_ID,
    I_MINI_SIZE,
    LSTORAGE_KEY,
    SEO_DEFAULT,
    SESSION_KEY,
} from 'src/constants';
import { IS_PRODUCTION } from 'src/constants/env';
import { IAppPage } from 'src/interface';
import {
    AdUtils,
    DeviceUtils,
    LocalStore,
    RedirectUtils,
    SessionStore,
    UrlUtils,
} from 'src/utils';
import {
    srcImpsTracking,
    trackingContent,
    trackingPage,
} from 'src/utils/tracking';
import CookieStore from 'src/utils/cookie';
import { useScrollRestoration } from 'src/hooks';
import store from 'src/redux/store';
import { loadAdNative } from 'src/redux/actions/ad';
import { AdDispatchActions, AppDispatchActions } from 'src/redux/actions';
import { PageWrap } from 'src/modules/shared/components/PageWrap';
import SEO from 'src/modules/shared/components/SEO';
import LayoutApp from '../layout';

import 'src/styles/globals.css';
import 'src/styles/index.scss';
import '@zmedia/bm-soccer-ui/styles/index.scss';

import '../polyfill';

Log.setLevel(IS_PRODUCTION ? process.env.APP_LOG_LEVEL : 0);

interface IProps extends AppProps {
    pageProps: any;
}

function MyApp({ Component, pageProps }: IProps) {
    const location = useLocation();
    const router = useRouter();

    const resp =
        pageProps.resp ||
        {
            // err: -pageProps.statusCode || -9999,
        };
    const data = resp.data || {};
    const ad = data.ad || {};
    const adMasthead = data.adMasthead || {};
    const hostName = pageProps.hostName || '';
    const isBlankLayout = pageProps.isBlankLayout || false;

    const firstLoadRef = useRef(true);
    useScrollRestoration(router);

    useEffect(() => {
        if (isBlankLayout && !pageProps.isRedirectPage) {
            return;
        }

        ReactGA4.gtag('consent', 'default', {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: 'denied',
        });

        const idTracking4 = DeviceUtils.isUtmZalo()
            ? GA4_TRACKING_ZALO_ID
            : GA4_TRACKING_MOBILE_ID;
        ReactGA4.initialize(idTracking4, {
            gaOptions: {
                name: 'trackingDefault',
            },
        });

        SessionStore.set(SESSION_KEY.SSID, new Date().getTime());
    }, []);

    useEffect(() => {
        const uid = UrlUtils.getParamUrl('uid');
        if (uid) {
            LocalStore.set(LSTORAGE_KEY.BM_UID, uid);
        }
    }, []);

    // useEffect(() => {
    //     const iframe = document.createElement('iframe');
    //     iframe.style.display = 'none';
    //     iframe.src = `https://dev-zls-player.zapps.me/hls/v1/index.html`;
    //     document.body.appendChild(iframe);
    // }, []);

    useEffect(() => {
        (window as any).preloadAds = false;
        (window as any)._BM.logImps = [];
        (window as any)._BM.logPageView = [];
        if (isBlankLayout && !pageProps.isRedirectPage) {
            return;
        }

        if (location.pathname) {
            ReactGA4.send({
                hitType: 'pageview',
                page: location.pathname,
                name: 'trackingDefault',
            });
        }

        if (
            (window as any).AdtimaBaomoi &&
            (window as any).AdtimaBaomoi.resetLayout
        ) {
            (window as any).AdtimaBaomoi.resetLayout();
        }
    }, [location.pathname]);

    useEffect(() => {
        if (data) {
            const _window: any = window;
            setTimeout(() => {
                trackingPage('mobile_all');
                if (UrlUtils.isResetFirstLoadPage(data.page)) {
                    (window as any).firstLoad = false;
                }
            }, 10);
            /*
                TODO cheat: xử lý tracking trang category
            */
            _window.categoryShortUrl = data.categoryShortUrl;
            _window.adtimaBMConfig = ad.adtimaConfig;
            _window.cate_path = data.categoryShortUrl || data.page;
            _window.article_id = data.content?.id;
            _window.adtimaBMConfig = {
                ...ad.adtimaConfig,
                ...adMasthead.adtimaConfig,
            };
            _window.adtimaTopicIds = adMasthead.topicId;

            if (adMasthead.zoneAdaptive) {
                const zoneAdaptive = {
                    [adMasthead.zoneId]: adMasthead.zoneAdaptive,
                };
                _window.adtimaBMConfig = {
                    ..._window.adtimaBMConfig,
                    zoneAdaptive: zoneAdaptive,
                };
            }

            if (data.content) {
                _window._BM.contentIdsExclude =
                    data.content.contentIdsExclude || [];

                _window._BM.contentIdsDuplicate =
                    data.content.contentIdsDuplicate || {};
            }
        }
    }, [data]);

    useEffect(() => {
        if (location.trigger !== 'popstate') {
            (window as any).renderSections = true;
        }
    }, [location]);

    useEffect(() => {
        if (data.page) {
            const page: IAppPage = {
                type: data.page,
                url: data.pageUrl,
                title: data?.head?.meta?.title || SEO_DEFAULT.title,
            };
            store.dispatch(AppDispatchActions.pageUpdate(page));
        }
    }, [data.pageUrl]);

    useEffect(() => {
        if (!ad.isNoAd && ad.zoneIds?.length) {
            document.addEventListener('adtima_freeze', (e: any) => {
                if (e.detail) {
                    const { fail_domains, api } = e.detail;
                    const isBlockAd =
                        /web.api.adtimaserver.vn|adtima-media.zascdn.me/.test(
                            fail_domains,
                        );
                    if (isBlockAd) {
                        (window as any)._BM.urlAds = api;
                    }
                }
            });

            const timer = firstLoadRef.current ? 500 : 0;
            setTimeout(() => {
                store.dispatch(loadAdNative(ad.zoneIds));
                (window as any).zoneIds = ad.zoneIds;
                firstLoadRef.current = false;
            }, timer); // delay for get install zalo
        }
    }, [ad.zoneIds]);

    useEffect(() => {
        if (typeof ad.isNoAd !== 'undefined') {
            store.dispatch(AdDispatchActions.isNoAdUpdate(ad.isNoAd));
        }
    }, [ad]);

    useEffect(() => {
        const { deviceType, osType, isIABM, isZalo, isBot } = pageProps;
        setTimeout(() => {
            store.dispatch(
                AppDispatchActions.deviceUpdate(
                    deviceType,
                    osType,
                    isIABM,
                    isZalo,
                    isBot,
                    window.self !== window.top,
                ),
            );
        }, 100);
    }, []);

    useEffect(() => {
        const params = {
            pageType: data.page,
            noAds: data?.ad?.isNoAd,
        };
        if ((window as any).AdtimaBaomoi) {
            if ((window as any).AdtimaBaomoi.config) {
                (window as any).AdtimaBaomoi.config._reqid = 0;
            }
            (window as any).paramsPreload = params;
            AdUtils.preloadAd(params);
        } else {
            (window as any).preloadAds = true;
            (window as any).adtimaPreload = params;
        }
    }, [data.pageUrl]);

    /*
     **Thêm tracking imps khi mở bài từ zalo, facebook,...
     */

    useEffect(() => {
        setTimeout(() => {
            const { content } = data;
            const source = srcImpsTracking(document.referrer);
            if (source && content) {
                trackingContent(
                    source,
                    content.contentId,
                    (window as any).dataAuth?.strUserId,
                );
            }
        }, 1000);
    }, []);

    // Detect trên ipad mini 6 size 744px
    useEffect(() => {
        const size = Math.max(
            document.documentElement.clientWidth || 0,
            window.innerWidth || 0,
        );

        const { deviceType } = pageProps;
        const isTablet = deviceType == DEVICES.TABLET;
        const isMobileFromCookie = CookieStore.get(COOKIE_KEY.BM_FORCE_IS_MB);
        if (isTablet && size <= I_MINI_SIZE && isMobileFromCookie !== 'true') {
            CookieStore.set(COOKIE_KEY.BM_FORCE_IS_MB, true);
            RedirectUtils.replaceUrl(location.pathname);
        }

        //remove cookie legacy (thử xóa với IOS server ko xóa được)
        CookieStore.set('_bm_content_read', '');
        CookieStore.set('_live1_bm_contents_read', '');
        CookieStore.set('_live1_bm_times_content_read', '');
    }, []);

    /*
     **Fix lỗi back không được khi có query string trên url
     **Ví dụ vào link https://baomoi.com/?utm_source=zalo&utm_medium=autorecommend&utm_campaign=zalo
     **sau đó bấm vào tab tin-moi sau đó bấm nút back từ trình duyệt => lỗi không quay về được.
     */
    useEffect(() => {
        if (location.pathname && location.search) {
            const objBM = {
                utmTracking: location.search,
                utmId: UrlUtils.getParamUrl('utm_id', location.search) || '',
                utmSource:
                    UrlUtils.getParamUrl('utm_source', location.search) || '',
                utmMedium:
                    UrlUtils.getParamUrl('utm_medium', location.search) || '',
                utmCampaign:
                    UrlUtils.getParamUrl('utm_campaign', location.search) || '',
            };
            (window as any)._BM = Object.assign({}, (window as any)._BM, objBM);
        }
    }, []);

    const robots =
        resp.err < 0 || UrlUtils.isSubDomain(hostName)
            ? {
                  robots: 'noindex,nofollow',
              }
            : {};

    return (
        <>
            <Head>
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=5, user-scalable=yes"
                />
                {pageProps.isRedirectPage ? (
                    <meta name="referrer" content="unsafe-url" />
                ) : (
                    <meta
                        name="referrer"
                        content="no-referrer-when-downgrade"
                    />
                )}
                <meta
                    property="al:ios:url"
                    content={`baomoi://web?source=Facebook&url=https://baomoi.com${data.pageUrl}`}
                />
                <meta
                    property="al:android:url"
                    content={`baomoi://web?source=Facebook&url=https://baomoi.com${data.pageUrl}`}
                />
                {data.preloadImageUrl && (
                    <link
                        rel="preload"
                        href={data.preloadImageUrl}
                        as="image"
                    />
                )}
            </Head>
            <SEO
                meta={Object.assign({}, data.head?.meta, robots)}
                jsonLds={data.head?.jsonLd}
                isRedirectPage={pageProps.isRedirectPage}
            />
            <Provider store={store}>
                {isBlankLayout ? (
                    <PageWrap error={resp.err}>
                        <Component {...pageProps} />
                    </PageWrap>
                ) : (
                    <LayoutApp
                        showMenu={data.showMenu}
                        sectionTop={data?.top}
                        breadcrumb={data?.breadcrumb}
                        footer={data.footer}
                        config={data.config}
                        page={data.page}
                        categoryIdOfPage={data.categoryIdOfPage}
                        options={data.options}
                        showFormSearch={data.showFormSearch}
                        ad={ad.isNoAd ? undefined : ad}
                        adMasthead={ad.isNoAd ? undefined : adMasthead}
                        isIABM={pageProps.isIABM || pageProps.isZalo}
                        isZalo={pageProps.isZalo}
                    >
                        <PageWrap error={resp.err}>
                            <Component {...pageProps} />
                        </PageWrap>
                    </LayoutApp>
                )}
            </Provider>
        </>
    );
}

export default MyApp;
